import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Box, Typography, Card, Stack, FormControlLabel, Checkbox, Alert, useMediaQuery,
  Button
} from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axios, { getErrorMessage } from 'utils/axios';
import { FormProvider, RHFTextField } from 'components/hook-form';
import { LoadingButton } from '@mui/lab';
import CRUD from 'utils/Crud';
import useIsMountedRef from 'hooks/useIsMountedRef';
import { useTheme } from '@mui/material/styles';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 340, sm: 400, md: 450 },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
};

const NewUserSchema = Yup.object().shape({
  do_swap: Yup.boolean(),
  do_order_update: Yup.boolean(),
  do_sweep: Yup.boolean(),
  password: Yup.string().required('Current admin password is required')
}).test(
  'at-least-one-checked',
  'Please select at least one operation to perform.',
  value => value && (value.do_swap || value.do_order_update || value.do_sweep)
);

TransactionOperationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleComplete: PropTypes.func.isRequired,
  txid: PropTypes.string.isRequired,
};

export default function TransactionOperationModal({ open, handleClose, handleComplete, txid }) {
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formError, setFormError] = useState(null);

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: {
      do_swap: false,
      do_order_update: false,
      do_sweep: false,
      password: ''
    },
  });

  const { handleSubmit, control, reset, formState: { errors, isSubmitting } } = methods;

  const onSubmit = async (values) => {
    setFormError(null);
    try {
      // Replace API URL with your backend endpoint
      await axios.post(`${CRUD.blockchain.retrySwapAction}`, {
        txid,
        ...values,
      });

      reset();
      enqueueSnackbar('Operation completed successfully!');
      handleComplete();
      
    } catch (error) {
      setFormError(getErrorMessage(error));
      enqueueSnackbar(getErrorMessage(error), { color: "error" });
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      hideBackdrop={false}
    >
      <Box sx={style}>
        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h2"
          textAlign="center"
          sx={{ mb: 1, fontWeight: 700 }}
        >
          Blockchain Transaction Operation
        </Typography>
        <Typography
          color="text.secondary"
          fontSize={15}
          textAlign="center"
          sx={{ mb: 3 }}
        >
          Please confirm this privileged operation by entering your password.<br />
          Choose at least one operation to perform:
        </Typography>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Card sx={{
              p: isMobile ? 2 : 3,
              borderRadius: 2,
              boxShadow: 2,
              bgcolor: 'grey.50',
            }}>
              <Stack spacing={2}>
                {/* Checkbox operations */}
                <Box>
                  <FormControlLabel
                    control={
                      <Controller
                        name="do_swap"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <Checkbox
                            color="primary"
                            {...field}
                            checked={Boolean(field.value)}
                          />
                        )}
                      />
                    }
                    label={<Typography fontWeight={600}>Retry Swap Action</Typography>}
                  /> 
                  <FormControlLabel
                    control={
                      <Controller
                        name="do_order_update"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <Checkbox
                            color="primary"
                            {...field}
                            checked={Boolean(field.value)}
                          />
                        )}
                      />
                    }
                    label={<Typography fontWeight={600}>Re-Value Order</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Controller
                        name="do_sweep"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <Checkbox
                            color="primary"
                            {...field}
                            checked={Boolean(field.value)}
                          />
                        )}
                      />
                    }
                    label={<Typography fontWeight={600}>Re-Sweep</Typography>}
                  />
                  {/* Custom error display */}
                  {errors && errors['at-least-one-checked'] && (
                    <Typography color="error" variant="body2">{errors['at-least-one-checked'].message}</Typography>
                  )}
                </Box>

                {/* Password input */}
                <RHFTextField
                  name="password"
                  label="Current Admin Password"
                  type="password"
                  autoComplete="current-password"
                  sx={{ mt: 1 }}
                  fullWidth
                  required
                />
                {errors.password?.message && (
                  <Typography color="error" variant="body2">{errors.password.message}</Typography>
                )}

                {/* Any form/server errors */}
                {formError && (
                  <Alert severity="error">
                    {formError}
                  </Alert>
                )}

                {/* Submit */}
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  fullWidth
                  size="large"
                  sx={{
                    mt: 2,
                    fontSize: 18,
                    fontWeight: 700,
                    py: 1.3,
                    borderRadius: 2
                  }}
                >
                  Confirm & Run Operation
                </LoadingButton>
                <Button
                  fullWidth
                  variant="text"
                  sx={{ mt: 1 }}
                  color="inherit"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </Stack>
            </Card>
        </FormProvider>
      </Box>
    </Modal>
  );
}
